import { useState, useEffect } from 'react';
import {
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import styles from 'styles/components/announcementBanner.module.scss';

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

function renderMessage(text) {
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, label, href] = match;
    const isInternal = href.startsWith('/') && !href.startsWith('//');
    parts.push(
      <a
        key={match.index}
        href={isInternal ? `/#${href}` : href}
        className={styles.link}
        {...(isInternal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {label}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
}

const DISMISSED_KEY = 'kl_dismissed_announcement';

const typeConfig = {
  info: { icon: <InfoCircleOutlined />, style: styles.info },
  warning: { icon: <WarningOutlined />, style: styles.warning },
  success: { icon: <CheckCircleOutlined />, style: styles.success },
};

function hashMessage(msg) {
  let h = 0;
  for (let i = 0; i < msg.length; i++) {
    h = (Math.imul(31, h) + msg.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}

const AnnouncementBanner = ({ banner }) => {
  const [dismissed, setDismissed] = useState(false);

  const hash = banner?.message ? hashMessage(banner.message) : null;

  useEffect(() => {
    if (hash && sessionStorage.getItem(DISMISSED_KEY) === hash) {
      setDismissed(true);
    } else {
      setDismissed(false);
    }
  }, [hash]);

  if (!banner || !banner.enabled || !banner.message || dismissed) return null;

  const type = banner.type && typeConfig[banner.type] ? banner.type : 'info';
  const { icon, style } = typeConfig[type];

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, hash);
    setDismissed(true);
  };

  return (
    <div className={`${styles.wrapper} ${style}`}>
      <div className={styles.inner}>
        <span className={styles.icon}>{icon}</span>
        <span className={styles.message}>{renderMessage(banner.message)}</span>
        <button
          className={styles.close}
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
        >
          <CloseOutlined />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
